// SVG / PDF / STL / TXT exporters. Laser files keep the original's convention: hairline red groove, black cut lines.

import { zipSync, strToU8 } from "fflate";
import type { LaserFile, LaserResult, MeshResult } from "../geom/types";

const HAIRLINE = 0.001; // inches

function svgPath(xy: Float32Array): string {
  const parts: string[] = [];
  for (let i = 0; i < xy.length; i += 2) parts.push(`${i ? "L" : "M"}${xy[i].toFixed(5)} ${xy[i + 1].toFixed(5)}`);
  return parts.join("");
}

export function laserFileToSvg(file: LaserFile, page: { w: number; h: number }): string {
  const out: string[] = [
    `<?xml version="1.0" encoding="UTF-8"?>`,
    `<svg xmlns="http://www.w3.org/2000/svg" width="${page.w}in" height="${page.h}in" viewBox="0 0 ${page.w} ${page.h}">`,
  ];
  for (const p of file.paths) out.push(`<path d="${svgPath(p)}" fill="none" stroke="#ff0000" stroke-width="${HAIRLINE}"/>`);
  for (const c of file.circles)
    out.push(`<circle cx="${c.cx.toFixed(5)}" cy="${c.cy.toFixed(5)}" r="${c.r.toFixed(5)}" fill="none" stroke="#000000" stroke-width="${HAIRLINE}"/>`);
  if (file.boundingBox) out.push(`<rect x="0" y="0" width="${page.w}" height="${page.h}" fill="none" stroke="#000000" stroke-width="${HAIRLINE}"/>`);
  out.push("</svg>");
  return out.join("\n");
}

export async function laserFileToPdf(file: LaserFile, page: { w: number; h: number }): Promise<Uint8Array> {
  const { jsPDF } = await import("jspdf"); // loaded on first PDF export only
  const doc = new jsPDF({ unit: "in", format: [page.w, page.h], orientation: page.w > page.h ? "landscape" : "portrait", compress: true });
  doc.setLineWidth(HAIRLINE);
  doc.setDrawColor(255, 0, 0);
  for (const p of file.paths) {
    if (p.length < 4) continue;
    doc.moveTo(p[0], p[1]);
    for (let i = 2; i < p.length; i += 2) doc.lineTo(p[i], p[i + 1]);
    doc.stroke();
  }
  doc.setDrawColor(0, 0, 0);
  for (const c of file.circles) doc.circle(c.cx, c.cy, c.r, "S");
  if (file.boundingBox) doc.rect(0, 0, page.w, page.h, "S");
  return new Uint8Array(doc.output("arraybuffer"));
}

export async function laserZip(res: LaserResult, base: string, format: "svg" | "pdf"): Promise<Uint8Array> {
  const files: Record<string, Uint8Array> = {};
  for (const [i, f] of res.files.entries()) {
    const name = `${base}${i}.${format}`;
    files[name] = format === "svg" ? strToU8(laserFileToSvg(f, res.page)) : await laserFileToPdf(f, res.page);
  }
  return zipSync(files, { level: 6 });
}

/** Binary STL. Normals computed from the winding. */
export function meshToStl(mesh: MeshResult): Uint8Array {
  const n = mesh.triCount, s = mesh.stlUnitScale, T = mesh.tris;
  const buf = new ArrayBuffer(84 + 50 * n);
  const dv = new DataView(buf);
  const header = `LaserCutRecord playground STL, units: ${mesh.stlUnitLabel}`;
  for (let i = 0; i < Math.min(80, header.length); i++) dv.setUint8(i, header.charCodeAt(i));
  dv.setUint32(80, n, true);
  let o = 84;
  for (let t = 0; t < n; t++) {
    const b = t * 9;
    const ux = T[b + 3] - T[b], uy = T[b + 4] - T[b + 1], uz = T[b + 5] - T[b + 2];
    const vx = T[b + 6] - T[b], vy = T[b + 7] - T[b + 1], vz = T[b + 8] - T[b + 2];
    let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len; ny /= len; nz /= len;
    dv.setFloat32(o, nx, true); dv.setFloat32(o + 4, ny, true); dv.setFloat32(o + 8, nz, true);
    for (let k = 0; k < 9; k++) dv.setFloat32(o + 12 + 4 * k, T[b + k] * s, true);
    dv.setUint16(o + 48, 0, true);
    o += 50;
  }
  return new Uint8Array(buf);
}

export function download(data: string | Uint8Array, name: string, type = "application/octet-stream") {
  const part: BlobPart = typeof data === "string" ? data : (data as Uint8Array<ArrayBuffer>);
  const url = URL.createObjectURL(new Blob([part], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** 16-bit PCM WAV of the simulated needle output. */
export function toWav(channels: Float32Array[], sampleRate: number): Uint8Array {
  const n = channels[0].length, ch = channels.length;
  const buf = new ArrayBuffer(44 + n * ch * 2);
  const dv = new DataView(buf);
  const str = (o: number, s: string) => [...s].forEach((c, i) => dv.setUint8(o + i, c.charCodeAt(0)));
  str(0, "RIFF"); dv.setUint32(4, 36 + n * ch * 2, true); str(8, "WAVE");
  str(12, "fmt "); dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, ch, true);
  dv.setUint32(24, sampleRate, true); dv.setUint32(28, sampleRate * ch * 2, true); dv.setUint16(32, ch * 2, true); dv.setUint16(34, 16, true);
  str(36, "data"); dv.setUint32(40, n * ch * 2, true);
  let o = 44;
  for (let i = 0; i < n; i++)
    for (let c = 0; c < ch; c++) {
      dv.setInt16(o, Math.max(-32768, Math.min(32767, Math.round(channels[c][i] * 32767))), true);
      o += 2;
    }
  return new Uint8Array(buf);
}
